/**
 * The access trail, turned into rows.
 *
 * SEPARATE FROM `activity.model.ts` ON PURPOSE, and the separation is the
 * ticket rather than a filing preference. That model shapes a Stream feed,
 * which answers "what happened" and is allowed to lose a line; this one shapes
 * `ondo.access_events`, which answers "who looked" and cannot. A single model
 * over both would invite a single list, and a merged list is only as
 * trustworthy as its weakest half.
 *
 * IT DOES SHARE ONE THING, AND DELIBERATELY: `stampOf`. The two tabs print the
 * same instant the same way, because two definitions would be one rounding
 * decision away from showing one event stamped two ways on two tabs of one
 * audit log. No `now` and no relative ages, for the reason that function gives
 * — "3 wks ago" is not auditable, and nothing here reads a clock, so these
 * tests need no frozen time and cannot go flaky.
 *
 * NOTHING HERE DECIDES WHAT MAY BE SHOWN. `workTitle` arriving null is the
 * SERVER having decided that this reader cannot open that work; this model
 * renders the absence and never fills it in.
 */

import type { AccessEvent } from "@ondo/shared";

import { initialsOf } from "../lib/names.ts";
import type { TagTone } from "../lib/Tag.tsx";
import {
  ACCESS_COPY,
  ACCESS_EMPTY,
  ACCESS_PAGE,
  ACCESS_SUBJECT_TAGS,
} from "./activity.constants.ts";
import { stampOf } from "./activity.model.ts";

/** Who, as the table's first column renders them. */
export interface AccessWhoCell {
  name: string;
  ondoId: string;
  initials: string;
  /** True for the reader's own line, which reads "You". */
  isSelf: boolean;
}

/** One line of the trail, ready to render. */
export interface AccessRow {
  id: string;
  who: AccessWhoCell;
  /** The firm, or the em-dash for a read somebody took as themselves. */
  actingFor: string;
  /** "One Minute Sculpture", "Collector list", "A record you cannot open". */
  lookedAt: string;
  /** The work's ONDO code, when there is one to print. Null otherwise. */
  code: string | null;
  /** Where the row leads, when the reader can actually open the thing. */
  href: string | null;
  tag: { label: string; tone: TagTone };
  glyph: string;
  /** "2 Mar 2026 · 09:14". Null for a stamp that will not parse. */
  when: string | null;
  isoTime: string;
}

export function toAccessRows(input: {
  events: AccessEvent[];
  /** '/gallery/me/work' — where a row about a work goes. */
  workBase: string;
  /** The reader's own ONDO id, so their own reads read "You". */
  viewerOndoId: string | null;
}): AccessRow[] {
  return input.events.map((event) => {
    const tag = ACCESS_SUBJECT_TAGS[event.subject];
    const isSelf =
      input.viewerOndoId !== null && event.actorOndoId === input.viewerOndoId;

    return {
      id: event.id,
      who: {
        name: isSelf ? ACCESS_COPY.self : event.actorName,
        ondoId: event.actorOndoId,
        initials: initialsOf(event.actorName, event.actorOndoId.slice(-2)),
        isSelf,
      },
      actingFor: event.actingName ?? ACCESS_COPY.asThemselves,
      lookedAt: lookedAt(event, tag.label),
      code: event.subject === "work_record" ? event.workOndoCode : null,
      href: hrefFor(event, input.workBase),
      tag: { label: tag.label, tone: tag.tone },
      glyph: tag.glyph,
      when: stampOf(event.occurredAt),
      isoTime: event.occurredAt,
    };
  });
}

/**
 * What the "Looked at" cell says.
 *
 * THE WORK'S TITLE IS NOT ALWAYS THERE, and that is a server decision this
 * function renders rather than second-guesses. A `work_record` row with a null
 * title is about a work the reader cannot open; naming it would make the trail
 * a lookup, so the cell says there is a record and declines to say which. The
 * row remains a complete answer to "who looked, and when", which is what it is
 * for.
 */
function lookedAt(event: AccessEvent, label: string): string {
  if (event.subject === "work_record") {
    return event.workTitle ?? ACCESS_COPY.unnamedWork;
  }

  // An export's count comes back on the trail read rather than from a second
  // request: it is the one fact about the file worth printing on a timeline,
  // and everything else about it — the scope, the record keys, the digest —
  // lives behind the exports screen OND-67 built.
  if (event.subject === "collector_list_export" && event.exportRecordCount !== null) {
    const records = event.exportRecordCount === 1 ? "1 record" : `${event.exportRecordCount} records`;

    return `${label} · ${records}`;
  }

  return label;
}

/**
 * Where a row leads, or nowhere.
 *
 * ONLY a work the reader can open gets a link, and the test for that is the
 * title the server did or did not send. Linking a row whose work came back
 * unnamed would hand the reader a 404 at best — and at worst would be this
 * screen inviting them to try a record the registry had just declined to name.
 */
function hrefFor(event: AccessEvent, workBase: string): string | null {
  if (event.subject !== "work_record" || event.subjectId === null) return null;
  if (event.workTitle === null) return null;

  return `${workBase}/${event.subjectId}`;
}

/** "12 lines" — counts what is SHOWN. */
export function accessCaption(shown: number): string {
  if (shown === 0) return "No lines";

  return shown === 1 ? "1 line" : `${shown} lines`;
}

/** Shown when the trail came back exactly full — the page is the ceiling. */
export function accessNoticeFor(loaded: number): string | null {
  return loaded >= ACCESS_PAGE ? ACCESS_COPY.capped : null;
}

/**
 * The empty state, and WHICH empty state.
 *
 * Two of them, because an empty trail means two different things depending on
 * the reader's rung, and saying the wrong one would be a false reassurance.
 * An administrator seeing nothing is told the firm has opened nothing. An
 * ordinary seat seeing nothing is told THEY have opened nothing, and that the
 * firm's own trail belongs to its administrators — because `access_events_read`
 * gives a viewer their own rows and no more, so "nothing here" from a viewer
 * must never be read as "nobody looked".
 */
export function accessEmptyFor(input: {
  total: number;
  administers: boolean;
}): { lead: string; detail: string } | null {
  if (input.total > 0) return null;

  return input.administers ? ACCESS_EMPTY.never : ACCESS_EMPTY.mineOnly;
}
